<template>
  <c-sidesheet id="new-transaction-modal" :active="displayModal" @close="close">
    <m-confirm ref="confirm" mode="danger" />

    <vacation-modal ref="vacationModal" :can-edit="false"></vacation-modal>

    <div v-show="!isLoadingData" class="modal-frame">
      <div class="modal-wrapper">
        <shadow-scroll-container ref="track" class="modal-track" :with-top-scroller="true">
          <div class="modal-frame__header">
            <h2 class="modal-frame__title">Новая проводка</h2>
          </div>

          <div v-if="recordData" class="calculation-sheet__filial">
            <svg-icon class="calculation-sheet__filial-icon" name="place" />
            {{ recordData.filial.value }}
          </div>

          <div v-if="!isEditableMode" class="row">
            <div class="col col-12 col-md-auto">
              <c-switcher
                v-model="transactionTypes.selectedType"
                class="h-switcher"
                type="group"
                :items="transactionTypes.types"
              />
            </div>
          </div>

          <div v-if="isEditableMode" class="row">
            <div class="col col-12 col-md-10 col-xl-6">
              <div class="modal-frame__worker">
                <img :src="`${worker.photo ? worker.photo : '/redesign/img/userpic.svg'}`" alt="" width="34" height="34" class="modal-frame__worker-photo">
                <div class="modal-frame__worker-text">
                  <div class="modal-frame__worker-name">{{ worker.name }}</div>
                  <div class="modal-frame__worker-posts">{{ recordData.post_name.value }}</div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="isEditableMode" class="row">
            <div class="col col-12 col-md-10 col-xl-6">
              <div class="modal-frame__transaction-type">
                <div class="modal-frame__transaction-type-name">
                  <span :style="{'color': transactionTypes.selectedItem.type === 'withdraw' ? '#ff238d' : '#12c971'}">{{ transactionTypes.selectedItem.short_name }}</span>
                  {{ transactionTypes.selectedItem.label }}
                </div>
                <div class="modal-frame__transaction-type-description">{{ transactionTypes.selectedItem.description }}</div>
              </div>
            </div>
          </div>

          <div v-if="!isEditableMode" class="row">
            <div class="col col-12 col-md-10 col-xl-6">
              <c-select-2
                v-model="transactionTypes.selectedItem"
                class="modal-frame__transaction-type"
                type="select"
                floating-label="Тип проводки"
                :clearable="true"
                :searchable="true"
                :items="transactionTypes.items"
                :useCustomColors="true"
                :required="isSubmitted && transactionTypesError"
                :required-message="isSubmitted && transactionTypesError"
                :required-mark="true"
              />
              <c-button
                v-if="transactionTypes.selectedItem && transactionTypes.selectedItem.description"
                v-b-popover.html="{
                  container: '#app',
                  trigger: 'hover click blur',
                  boundary: 'window',
                  content: 
                  `
                    <div style='font-size: 11px; line-height: 120%; color: ${this.transactionTypes.selectedItem.type === 'deposit' ? '#12C971;' : '#FF238D;'}'>
                      ${this.transactionTypes.selectedItem.type === 'deposit' ? 'Начисления' : 'Списания'}
                    </div>
                    <div style='font-size: 13px; font-weight: 700'>${this.transactionTypes.selectedItem.label}</div>
                    <div style='margin: 5px 0 15px;'>${this.transactionTypes.selectedItem.description}</div>
                    <div style='margin: 5px 0 0;'>${this.selectedTypeConditions}</div>
                    <button style='margin-top: 15px;' type='button' class='c-button close-popover c-button-ok'>
                      <span class='c-button-label'>Ок</span>
                    </button>
                  `,
                  placement: 'top',
                }"
                class="form-group__item-description form-group__item-description--desktop"
                size="iconic"
                icon="info-new"
              />
              <c-button
                v-if="transactionTypes.selectedItem && transactionTypes.selectedItem.description"
                v-b-popover.html="{
                  container: '#app',
                  trigger: 'click blur',
                  boundary: 'window',
                  content: 
                  `
                    <div style='font-size: 11px; line-height: 120%; color: ${this.transactionTypes.selectedItem.type === 'deposit' ? '#12C971;' : '#FF238D;'}'>
                      ${this.transactionTypes.selectedItem.type === 'deposit' ? 'Начисления' : 'Списания'}
                    </div>
                    <div style='font-size: 13px; font-weight: 700'>${this.transactionTypes.selectedItem.label}</div>
                    <div style='margin: 5px 0 15px;'>${this.transactionTypes.selectedItem.description}</div>
                    <div style='margin: 5px 0 15px;'>${this.selectedTypeConditions}</div>
                    <button type='button' class='c-button close-popover'>
                      <span class='c-button-label'>Ок</span>
                    </button>
                  `,
                  placement: 'top',
                }"
                class="form-group__item-description form-group__item-description--mobile"
                icon="info-new"
                label="Показать описание"
              />
            </div>
          </div>

          <div v-if="!isEditableMode" class="row">
            <div class="col col-12 col-md-10 col-xl-6">
              <c-select-2
                v-model="workersList.selectedEmployee"
                type="select"
                floating-label="Сотрудник"
                :clearable="true"
                :searchable="true"
                :items="workersList.defaultItems"
                :required="isSubmitted && selectedEmployeeError"
                :required-message="isSubmitted && selectedEmployeeError"
                :required-mark="true"
              />
            </div>
          </div>

          <div class="row" v-if="balances.worker_id">
            <div class="col col-12 col-md-10 col-xl-6">
              <div class="form-group__item-balances">
                <div class="form-group__item-balances-item">
                  <div class="form-group__item-balances-item-header">
                    <div class="form-group__item-balances-item-number" :class="{'positive': balances.personal_amount > 0, 'negative': balances.personal_amount < 0 }">
                      {{ formatNumber(balances.personal_amount) }} ฿
                    </div>
                    <c-button
                      v-b-popover.html="{
                        container: '#app',
                        trigger: 'hover click blur',
                        boundary: 'window',
                        content: this.filialsWithBalances,
                        placement: 'top',
                      }"
                      class="form-group__balances-description"
                      size="iconic"
                      icon="info-new"
                    />
                  </div>
                  <div class="form-group__item-balances-item-name">Личный счет</div>
                </div>
                <div class="form-group__item-balances-item">
                  <div class="form-group__item-balances-item-header">
                    <div class="form-group__item-balances-item-number" :class="{'positive': balances.deposit_amount > 0, 'negative': balances.deposit_amount < 0 }">
                      {{ formatNumber(balances.deposit_amount) }} ฿
                    </div>
                  </div>
                  <div class="form-group__item-balances-item-name">Депозит</div>
                </div>
                <div class="form-group__item-balances-item">
                  <div class="form-group__item-balances-item-header">
                    <div class="form-group__item-balances-item-number" :class="{'positive': balances.accountable_amount > 0, 'negative': balances.accountable_amount < 0 }">
                      {{ formatNumber(balances.accountable_amount) }} ฿
                    </div>
                  </div>
                  <div class="form-group__item-balances-item-name">Подотчет</div>
                </div>
              </div>
            </div>
          </div>

          <!-- <div class="row">
            <div class="col col-12 col-md-5 col-xl-3">
              <c-input
                v-model="filials.selectedFilial"
                type="select"
                floating-label="Филиал"
                :clearable="true"
                :searchable="true"
                :items="this.filials.formatedFilials"
                :useCustomColors="true"
                :required="isSubmitted && filialError"
                :required-condition="isSubmitted && filialError"
                :required-mark="true"
                :class="{ 'is-invalid': isSubmitted && filialError }"
                :disabled="!workersList.selectedEmployee"
              />
            </div>
            <div class="col col-12 col-md-5 col-xl-3">
              <c-input
                v-model="posts.selectedPost"
                type="select"
                floating-label="Должность"
                :clearable="true"
                :searchable="true"
                :items="workersList.selectedEmployee && workersList.selectedEmployee.posts ? workersList.selectedEmployee.posts : []"
                :disabled="!workersList.selectedEmployee"
                :required-mark="true"
                :class="{ 'is-invalid': isSubmitted && postError }"
                :required="isSubmitted && postError"
                :required-condition="isSubmitted && postError"
              />
            </div>
          </div> -->
          
          <div v-if="!transactionTypeWithoutReason" class="row">
            <div class="col col-12 col-md-10 col-xl-6">
              <c-input
                v-model="transactionReasons.selectedReason"
                class="modal-frame__transaction-type"
                type="select"
                :floating-label="transactionReason"
                :clearable="true"
                :searchable="true"
                :items="transactionReasons.items"
                :groups="transactionReasons.groups"
                :useCustomColors="true"
                :required="isSubmitted && transactionReasonsError"
                :required-condition="isSubmitted && transactionReasonsError"
                :required-mark="true"
                :class="{ 'is-invalid': isSubmitted && transactionReasonsError }"
                :disabled="!transactionTypes.selectedItem || (!posts.selectedPost && (transactionTypes.selectedItem.short_name === 'ШТРФ' || transactionTypes.selectedItem.short_name === 'ПРЕМ'))"
              />
              <c-button
                v-if="transactionReasons.selectedReason && (transactionReasons.selectedReason.description || transactionReasons.selectedReason.subLabel) && (transactionTypes.selectedItem.short_name !== 'ШТРФ' && transactionTypes.selectedItem.short_name !== 'ПРЕМ')"
                v-b-popover.html="{
                  container: '#app',
                  trigger: 'click blur',
                  boundary: 'window',
                  content: this.transactionReasons.selectedReason.description || this.transactionReasons.selectedReason.subLabel,
                  placement: 'top',
                }"
                class="form-group__item-description form-group__item-description--desktop"
                size="iconic"
                icon="info-new"
              />
              <c-button
                v-if="transactionReasons.selectedReason && (transactionReasons.selectedReason.description || transactionReasons.selectedReason.subLabel) && (transactionTypes.selectedItem.short_name !== 'ШТРФ' && transactionTypes.selectedItem.short_name !== 'ПРЕМ')"
                v-b-popover.html="{
                  container: '#app',
                  trigger: 'click blur',
                  boundary: 'window',
                  content: this.transactionReasons.selectedReason.description || this.transactionReasons.selectedReason.subLabel,
                  placement: 'top',
                }"
                class="form-group__item-description form-group__item-description--mobile"
                icon="info-new"
                label="Показать описание"
              />
              <c-button
                v-if="transactionReasons.selectedReason && transactionReasons.selectedReason.file && (transactionTypes.selectedItem.short_name === 'ОТП' || transactionTypes.selectedItem.short_name === 'БЛН')"
                class="form-group__item-description form-group__item-description--desktop"
                size="iconic"
                icon="info-new"
                @click="openVacation"
              />
            </div>
            <!-- <div v-if="transactionReasons.selectedReason && (!transactionTypes.selectedItem.short_name === 'ОТП' && !transactionTypes.selectedItem.short_name === 'БЛН')" class="col col-12 col-md-1 col-xl-1"> -->
              <show-description
                v-if="transactionTypes.selectedItem.short_name === 'ШТРФ'"
                class="show-description"
                :value="transactionReasons.selectedReason && transactionReasons.selectedReason.value"
                :list="fineDescriptions"
                :type="'mini'"
              />
              <show-description
                v-if="transactionTypes.selectedItem.short_name === 'ПРЕМ'"
                class="show-description"
                :value="transactionReasons.selectedReason && transactionReasons.selectedReason.value"
                :list="bonusDescriptions"
                :type="'mini'"
              />
            <!-- </div> -->
          </div>

          <div class="row row-sum">
            <div class="col col-6 col-md-3 col-xl-2">
              <c-input
                type="price-number"
                v-model="transactionAmount"
                label="Сумма, ฿"
                placeholder="0"
                :max-length="12"
                :clearable="true"
                :required="isSubmitted && transactionAmountError"
                :required-message="isSubmitted && transactionAmountError"
                :input-text-condition="isSubmitted && transactionAmountError"
                 :required-mark="true"
              />
            </div>
            <div class="col col-6 col-md-3 col-xl-2">
              <c-input
                ref="dateInput"
                v-model="transactionDate.value"
                label="Дата учетная"
                :editable="true"
                :clearable="true"
                type="date"
                format="DD.MM.YYYY, dddd"
                icon="calendar"
                placeholder="00.00.0000"
                :required="isSubmitted && transactionDate.error"
                :required-message="isSubmitted && transactionDate.error"
                :required-mark="true"
                :icon-right="true"
                @invalidDate="sendErrorDateMessage"
              />
            </div>
          </div>

          <div class="row">
            <div class="col col-12 col-md-10 col-xl-6">
              <c-select-2
                v-model="workersList.selectedWorker"
                type="select"
                floating-label="Кто принял решение"
                :clearable="true"
                :searchable="true"
                :items="workersList.items"
              />
            </div>
          </div>

          <div class="row">
            <div class="col col-12 col-md-10 col-xl-6">
              <c-select-2
                v-model="companies.selectedCompany"
                class="modal-frame__transaction-type"
                type="select"
                floating-label="Юр. лицо"
                :clearable="true"
                :searchable="true"
                :items="companies.items"
                :useCustomColors="true"
                :required="isSubmitted && companiesError"
                :required-message="isSubmitted && companiesError"
                :required-mark="true"
              />
            </div>
          </div>

          <div v-if="transactionTypeIsZp" class="row">
            <div class="col col-12 col-md-10 col-xl-6">
              <c-select-2
                v-model="moneySourcesList.selectedMoneySource"
                type="select"
                floating-label="Источник д/с"
                :clearable="true"
                :searchable="true"
                :items="moneySourcesListFiltered"
                :required="isSubmitted && selectedMoneySourceError"
                :required-message="isSubmitted && selectedMoneySourceError"
                :required-mark="true"
              />
            </div>
          </div>

          <div class="row">
            <div class="col col-12 col-md-10 col-xl-6">
              <c-input
                ref="comment"
                type="textarea"
                v-model="comment"
                :floating-label="'Комментарий'"
                :clearable="true"
                max-length="255"
              />
            </div>
          </div>

          <div class="modal-frame__actions">
            <c-button class="modal-frame__action" label="Отменить" size="large" @click="close" />
            <c-button class="modal-frame__action" label="Сохранить" size="large" color="primary" @click="submit" :disabled="submitIsDisabled" />
          </div>
        </shadow-scroll-container>
      </div>
      <div class="modal-actions">
        <c-button size="large" label="Отменить" @click="close" />
        <c-button label="Сохранить" color="primary" size="large" @click="submit" />
      </div>
    </div>

    <c-loader
      v-if="isLoadingData"
      label="Данные загружаются"
      :active="isLoadingData"
      :height="'400'"
    />

  </c-sidesheet>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import CInput from "@/components/controls2/Input";
import CTextarea from "@/components/textarea";
import CButton from "@/components/controls2/Button";
import CCheckbox from "@/components/checkbox";
import CSidesheet from "@/components/sidesheet";
import MConfirm from "@/components/modals/confirm-modal";
import FileLoader from "@/components/file-loader";
import TopButton from "@/components/top-button";
import ShadowScrollContainer from "@/components/shadow-scroll-container";
import Input2 from "@/components/controls2/Input";
import Switcher from "@/components/controls2/Switcher";
import Select2 from "@/components/controls2/Select";
import { FineReasonDescription } from "@/entities/models/fine-reason";
import ShowDescription from "@/components/show-description";
import VacationModal from "@/pages/financial-transactions/components/vacation-modal";
import Loader from "@/components/loader";
import SvgIcon from "@/components/svg-icon";
import formatDate   from "@/utils/date/format";

export default {
  name: "new-transaction-modal",
  components: {
    CInput,
    CTextarea,
    CButton,
    CCheckbox,
    CSidesheet,
    MConfirm,
    FileLoader,
    TopButton,
    ShadowScrollContainer,
    "c-input-2": Input2,
    "c-switcher": Switcher,
    "c-select-2": Select2,
    ShowDescription,
    VacationModal,
    "c-loader": Loader,
    SvgIcon,
  },
  async created() {
    document.addEventListener("click", (e) => {
      if (e.target.closest('.close-popover')) {
        this.$root.$emit("bv::hide::popover");
      }
    });
  },
  data() {
    return {
      isSubmitted: false,
      displayModal: false,
      transactionAmount: '',
      comment: '',
      submitIsDisabled: false,
      vacationWorker: "",
      isEditableMode: false,
      isLoadingData: false,
      transactionId: null,
      recordData: null,
      isEditInit: false,

      balances: {},

      transactionDate: {
        value: '',
        error: '',
      },

      worker: {
        id: 0,
        filial: '',
        filial_id: 0,
        postCodes: [],
        photo: '', 
        name: '', 
        posts: ''
      },

      filials: {
        selectedFilial: '',
        allFilials: [],
        formatedFilials: [],
      },

      moneySourcesList: {
        selectedMoneySource: '',
        items: [],
        defaultItems: [],
      },

      posts: {
        selectedPost: '',
        allPosts: [],
      },

      transactionTypes: {
        types: [
          { label: 'Списание', value: 'withdraw' },
          { label: 'Начисление', value: 'deposit', borderColor: 'green' },
        ],
        selectedType: "withdraw",
        items: [],
        selectedItem: '',
      },

      transactionReasons: {
        items: [],
        selectedReason: '',
        groups: [],
      },

      workersList: {
        defaultItems: [],
        items: [],
        selectedWorker: '',
        selectedEmployee: ''
      },

      companies: {
        items: [],
        selectedCompany: '',
      },

      currentSheet: ""
    };
  },
  computed: {
    ...mapState({
      userId: (state) => state.personalEmployee.transactions.userId,
      transactionTypesFromStore: (state) => state.personalEmployee.transactions.transactionTypes,
      allTransactionTypesFromStore: (state) => state.personalEmployee.transactions.allTransactionTypes,
      transactionReasonsFromStore: (state) => state.personalEmployee.transactions.transactionReasons,
      allTransactionReasonsFromStore: (state) => state.personalEmployee.transactions.allTransactionReasons,
      workersListFromStore: (state) => state.personalEmployee.transactions.workersList,
      currentUserWorker: (state) => state.personalEmployee.transactions.currentUserWorker,
      companiesFromStore: (state) => state.personalEmployee.transactions.companies,
      employeeInfo: (state) => state.personalEmployee.transactions.employeeInfo,
      filialsListFromStore: (state) => state.personalEmployee.transactions.filialsList,
      moneySourceListFromStore: (state) => state.personalEmployee.collections.moneySourceList,
    }),
    ...mapGetters({
      finesCollection: "finesAndBonuses/finesCollection",
      bonusesCollection: "finesAndBonuses/bonusesCollection",
      finesList: "referenceReasonsForFines/getFinesCollection",
      bonusesList: "referenceReasonsForBonuses/getBonusesCollection",
    }),
    fineDescriptions() {
      return this.finesList.map((fine) => FineReasonDescription(fine));
    },
    bonusDescriptions() {
      return this.bonusesList.map((bonus) => FineReasonDescription(bonus));
    },
    transactionTypeIsZp() {
      if (this.transactionTypes.selectedItem) {
        return this.transactionTypes.selectedItem.short_name === "ЗП КАРТ" || this.transactionTypes.selectedItem.short_name === "ЗП НАЛ" || this.transactionTypes.selectedItem.short_name === "НАЧ ПДОТЧ"
      } else {
        return false
      }
    },
    transactionTypeWithoutReason() {
      if (this.transactionTypes.selectedItem) {
        return this.transactionTypes.selectedItem.short_name === "ЗП КАРТ" || this.transactionTypes.selectedItem.short_name === "ЗП НАЛ" || this.transactionTypes.selectedItem.short_name === "НАЧ ДЕП" || this.transactionTypes.selectedItem.short_name === "СПС ДЕП" || this.transactionTypes.selectedItem.short_name === "ВОЗВ ПДОТЧ" || this.transactionTypes.selectedItem.short_name === "НАЧ ПДОТЧ"
      } else {
        return false
      }
    },
    moneySourcesListFiltered() {
      console.log(777, this.transactionTypes);
      if (this.transactionTypes.selectedItem && this.transactionTypes.selectedItem.conditions && this.transactionTypes.selectedItem.conditions.length) {
        var all = false;
        var ids = [];
        this.transactionTypes.selectedItem.conditions.forEach(c => {
          if (c.money_sources && c.money_sources.length) {
            var allow = false;
            if (!c.filials || c.filials.length==0) {
              allow = true;
            } else if (!this.filials.selectedFilial || !this.filials.selectedFilial.value) {
              allow = false;
            } else {
              allow = c.filials.findIndex((f)=>f.id.toString()==this.filials.selectedFilial.value.toString())>=0;
            }
            if (allow) {
              c.money_sources.forEach(m => ids.push(m.id));
            }
          } else {
            if (!c.filials || c.filials.length == 0) {
              all = true;
            } else if (c.filials.findIndex((f)=>f.id.toString()==this.filials.selectedFilial.value.toString())>=0){
              all = true;
            }
          }

        })
        return  this.moneySourcesList.items.filter((c) => {return all || ids.findIndex((i)=>i.toString()==c.value.toString())>=0;})
      }
      return this.moneySourcesList.items;
    },

    selectedTypeConditions() {
      let array = [];

      if (this.transactionTypes.selectedItem && this.transactionTypes.selectedItem.conditions) {
        this.transactionTypes.selectedItem.conditions.forEach(condition => {
          let filials = [];
          let moneySources = [];
          if (condition.filials && condition.filials.length) {
            condition.filials.forEach(filial => {
              filials.push(filial.name)
            })
          }
          if (condition.money_sources && condition.money_sources.length) {
            condition.money_sources.forEach(money_source => {
              moneySources.push(money_source.name)
            })
          }
          // array.push([...filials, ...moneySources].join(', '))
          array.push(`${filials.join(", ")} — ${moneySources.join(", ")}`)
        })
      }

      return array.join('<br />');
    },
    transactionTypesError() {
      return !this.transactionTypes.selectedItem && "Обязательное поле";
    },
    selectedEmployeeError() {
      return !this.workersList.selectedEmployee && "Обязательное поле";
    },
    selectedMoneySourceError() {
      return !this.moneySourcesList.selectedMoneySource && "Обязательное поле";
    },
    filialError() {
      return !this.filials.selectedFilial && "Обязательное поле";
    },
    postError() {
      return !this.posts.selectedPost && "Обязательное поле";
    },
    transactionReasonsError() {
      return !this.transactionReasons.selectedReason && "Обязательное поле";
    },
    transactionAmountError() {
      return !this.transactionAmount && "Обязательное поле" || Number(this.transactionAmount) === 0 && "Введите сумму больше нуля" || Number(this.transactionAmount) > 9999999.99 && "Не больше 9999999.99" ;
    },
    companiesError() {
      return !this.companies.selectedCompany && "Обязательное поле";
    },
    transactionReason() {
      console.log('transactionReason', this.transactionTypes.selectedItem)
      return this.transactionTypes.selectedItem && this.transactionTypes.selectedItem.dropdown_name ? this.transactionTypes.selectedItem.dropdown_name : 'Причина проводки'
    },
    filialsWithBalances() {
      let array = [];

      this.filials.allFilials.forEach(filial => {
        if (filial.amount !== null) {
          array.push(`
            <div style='display: flex; align-items: center; justify-content: space-between;'>
              <span>${filial.label}</span>
              <span style='margin-left: 10px; color:${filial.amount > 0 ? '#12C971;' : filial.amount < 0 ? '#FF238D;' : '#8E9CBB;' }'>${filial.amount} ฿</span>
            </div>`)
        }
      })

      array.push('<button class="tablet close-popover">Ok</button>')

      return array.join('<br/>')
    }
  },
  methods: {
    onScroll(scrollTop) {
      this.scrolled = scrollTop > 150;
    },

    openVacation() {
      this.$refs.vacationModal.open({
        vacation: this.transactionReasons.selectedReason,
        worker: this.vacationWorker,
      });
    },

    formatNumber(number) {
      return String(number).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")
    },

    async setData(data, currentSheet = {}) {
      this.isEditInit = true;
      console.log('currentSheetcurrentSheetcurrentSheet', currentSheet)
      // this.transactionId = data.id.value;

      let worker;

      await this.$store.dispatch("financialTransactions/getWorkerById", Number(data.worker.worker.id)).then(res => {
        worker = res;
      })

      if (worker) {
        this.worker = {
          photo: worker.photo, 
          name: worker.fio, 
          filial: worker.filial_name, 
          filial_id: worker.filial_id, 
          filial_name: worker.filial_name, 
          postsString: worker.post_names,
          posts: worker.posts.map(post => ({
            value: post.code,
            label: post.name
          })),
          value: data.worker.worker.id,
          label: worker.fio,
          company_id: worker.company_id,
          filial_company_id: data.filial_company_id.value,
        }
      }

      this.workersList.selectedEmployee = this.worker;

      let transactionTypeInList = this.allTransactionTypesFromStore.find(transactionType => transactionType.id === data.transaction_type_id.value)

      this.transactionTypes.selectedItem = {
        value: data.transaction_type_id.value,
        label: data.transaction_type_name.value,
        short_name: data.transaction_type.value,
        type: data.type.value,
        description: transactionTypeInList && transactionTypeInList.description ? transactionTypeInList.description : null,
        reasons: transactionTypeInList && transactionTypeInList.reasons ? transactionTypeInList.reasons : [],
        conditions: transactionTypeInList && transactionTypeInList.conditions ? transactionTypeInList.conditions : [],
        dropdown_name: transactionTypeInList.dropdown_name,
      }

      if (this.transactionTypes.selectedItem.short_name === "ШТРФ" || this.transactionTypes.selectedItem.short_name === "ПРЕМ") {
          // await this.$store.dispatch("finesAndBonuses/fetchFinesAndBonuses", this.posts.selectedPost ? this.posts.selectedPost.value : "");
          if (this.transactionTypes.selectedItem.short_name === "ШТРФ") {
              this.transactionReasons.items = this.finesCollection.items;
              this.transactionReasons.groups = this.finesCollection.groups;
            }
            if (this.transactionTypes.selectedItem.short_name === "ПРЕМ") {
              this.transactionReasons.items = this.bonusesCollection.items;
              this.transactionReasons.groups = this.bonusesCollection.groups;
            }
        } else if (this.transactionTypes.selectedItem.short_name === "ОТП" && this.workersList.selectedEmployee) {
          await this.getVacationSickList(this.workersList.selectedEmployee.value, "vacation")
        } else if (this.transactionTypes.selectedItem.short_name === "БЛН" && this.workersList.selectedEmployee) {
          await this.getVacationSickList(this.workersList.selectedEmployee.value, "sick")
        } else if (this.transactionTypes.selectedItem.reasons && this.transactionTypes.selectedItem.reasons.length) {
          this.transactionReasons.items = this.transactionTypes.selectedItem.reasons.map(reason => ({
            value: reason.id,
            label: reason.name,
            subLabel: reason.description,
          }))
        } else {
          this.formatTransactionReasons()
          this.transactionReasons.selectedReason = this.transactionReasons.items.find(reason => Number(reason.value) === Number(data.reason_id.value))
        }

      // this.transactionDate.value = data.date_created.value
      // await this.$refs.dateInput.setDateInputTypeDate(data.date_created.value);

      if (data.author && data.author.worker) this.workersList.selectedWorker = this.workersList.items.find(worker => Number(worker.value) === data.author.worker.id)

      setTimeout(() => {
        // this.companies.selectedCompany = this.companies.items.find(company => Number(company.value) === Number(data.company_id.value))
        // this.transactionAmount = data.amount.value < 0 ? data.amount.value * -1 : data.amount.value
        // this.moneySourcesList.selectedMoneySource = this.moneySourcesList.items.find(moneySource => moneySource.value === data.money_source_id.value)
      }, 10);

      // this.comment = data.comment.value
      // this.$refs.comment.setValue(data.comment.value)
      // this.$refs.comment.textValue = data.comment.value
      // this.$refs.comment.$refs.textareaValue.innerHTML = data.comment.value
    },

    async open(data, currentSheet, params = {}) {
      this.isLoadingData = true;
      if (data) {
        this.isEditableMode = true;
        this.recordData = data;
      }
      console.log('this.recordData', this.recordData.post_code.value)
      this.currentSheet = currentSheet;

      this.displayModal = true;

      //

      // this.worker.id = this.$route.params.id;

      this.filials.formatedFilials = this.filials.allFilials;

      // await this.$store.dispatch("personalEmployee/transactions/fetchNewWorkersList");

      this.formatTransactionTypes();

      console.log(this.allTransactionTypesFromStore)

      // await this.$store.dispatch("personalEmployee/transactions/fetchTransactionTypes", this.isEditableMode ? data.type.value : this.transactionTypes.selectedType);

      // await this.$store.dispatch("personalEmployee/transactions/fetchFilialsList").then(data => {
      //   this.filials.allFilials = this.filialsListFromStore
      //     .sort((a, b) => b.sort - a.sort)
      //     .map((filial) => ({
      //       value: filial.id,
      //       label: filial.name,
      //     }))
      // })

      this.filials.allFilials = this.filialsListFromStore
        .sort((a, b) => b.sort - a.sort)
        .map((filial) => ({
          value: filial.id,
          label: filial.name,
        }))

      // await this.$store.dispatch("personalEmployee/getPostList").then(res => {
      //   const data = res.response;
        
      //   this.posts.allPosts = data.map(post => ({
      //     value: post.id,
      //     label: post.name
      //   }))
      // })

      this.formatTransactionReasons();

      // await this.$store.dispatch("personalEmployee/transactions/fetchTransactionReasons", this.isEditableMode ? data.type.value : this.transactionTypes.selectedType);

      // this.$refs.dateInput.setCurrentDateInputTypeDate();

      this.formatWorkersList();

      // if (!this.currentUserWorker) await this.$store.dispatch("personalEmployee/transactions/fetchCurrentUserWorker");
      // if (this.currentUserWorker) console.log(this.currentUserWorker);

      // await this.$store.dispatch("personalEmployee/transactions/fetchEmployeeInfo", this.worker.id);

      // await this.$store.dispatch("personalEmployee/transactions/fetchCompanies");

      // await this.$store.dispatch("personalEmployee/transactions/fetchMoneySourcesList").then(res => {
      //   this.moneySourcesList.defaultItems = this.moneySourceListFromStore.map(moneySource => ({
      //     value: moneySource.id,
      //     label: moneySource.name
      //   }))

      //   this.moneySourcesList.items = this.moneySourcesList.defaultItems
      // })

      this.moneySourcesList.defaultItems = this.moneySourceListFromStore.map(moneySource => ({
        value: moneySource.id,
        label: moneySource.name
      }))

      this.moneySourcesList.items = this.moneySourcesList.defaultItems

      // if (!this.finesCollection) await this.$store.dispatch("referenceReasonsForFines/getFinesCollection");
      // if (!this.bonusesCollection) await this.$store.dispatch("referenceReasonsForBonuses/getBonusesCollection");

      // await this.$store.dispatch("personalEmployee/getUserInfo", this.worker.id).then(res => {
        // if (res && res.response) {
        //   const data = res.response;
        //   this.worker.filial = data.filial
        //   this.worker.filial_id = data.filial_id
        //   this.worker.postCodes = data.postCodes

        //   // Filials
        //   this.filials.allFilials = this.filials.allFilials.filter(filial => Number(filial.value) !== this.worker.filial_id)
        //   this.filials.allFilials.unshift({
        //     value: this.worker.filial_id, 
        //     label: this.worker.filial, 
        //     formatLabel: `<div style='font-weight: 500; border-bottom: 1px solid #DFE4EF;padding-bottom: 10px;'>${this.worker.filial}</div>`,
        //   })
        //   this.filials.selectedFilial = this.filials.allFilials[0]

        //   // Posts
        //   this.posts.allPosts = this.worker.postCodes.map(post => ({
        //     value: post.post_code,
        //     label: post.name
        //   }))

        //   this.posts.selectedPost = this.posts.allPosts[0]
        // }
      // })

      //

      // if (params.focusOnDescription) {
      //   this.$nextTick(() => {
      //     const field = this.$el.querySelector(".description");
      //     if (field) field.querySelector("textarea").focus();
      //   });
      // }

      this.$store.dispatch("financialTransactions/getWorkerBalances", data.worker.worker.id).then(res => {
        this.balances = res;
      })
      this.$store.dispatch("financialTransactions/getFilialsWithWorkerBalances", data.worker.worker.id).then(res => {
        this.filials.allFilials = this.filials.allFilials.map(filial => {
          let findedFilial = res.find(otherFilial => String(otherFilial.id) === String(filial.value))
          let filialAmount = findedFilial.amount;

          return ({
            ...filial,
            amount: filialAmount,
            formatLabel: filialAmount !== null ? `<div style="display: flex; align-items: center; justify-content: space-between;"><span>${filial.label}</span><span style='white-space: nowrap; color:${filialAmount > 0 ? '#12C971;' : filialAmount < 0 ? '#FF238D;' : '#8E9CBB;' }'>${filialAmount} ฿</span></div>` : filial.label
          })
        })
      })


      if (data) {
        await this.setData(data, currentSheet);
        setTimeout(() => {
          this.isEditInit = false
        }, 1000);
      }

      this.isLoadingData = false;
    },

    close() {
      this.displayModal = false;
      this.reset();
    },

    reset() {
      this.isSubmitted = false;
      this.isEditableMode = false;

      this.transactionAmount = '';

      this.comment = '';
      this.$refs.comment.setValue('')
      this.$refs.comment.textValue = '';
      this.$refs.comment.$refs.textareaValue.innerHTML = '';

      this.transactionDate.value = '';
      this.transactionDate.error = '';
      this.$refs.dateInput.clear();

      this.worker.id = 0;
      this.worker.filial = '';
      this.worker.filial_id = 0;
      this.worker.postCodes = [];

      this.filials.selectedFilial = '';
      this.filials.allFilials = [];
      
      this.posts.selectedPost = '';
      this.posts.allPosts = [];

      this.transactionTypes.selectedType = "withdraw";
      this.transactionTypes.items = [];
      this.transactionTypes.selectedItem = '';

      this.transactionReasons.items = [];
      this.transactionReasons.selectedReason = '';
      
      this.workersList.items = [];
      this.workersList.selectedWorker = '';
      this.workersList.selectedEmployee = '';

      // this.companies.items = [];
      this.companies.selectedCompany = '';
    },

    getParams() {
      let id_reason;
      if (this.transactionReasons.selectedReason) {
        id_reason = Number(this.transactionReasons.selectedReason.value)
      }
      let bonus_fine_type_id = null;
      let vacation_id = null;

      if (this.transactionTypes.selectedItem.short_name === "ШТРФ" || this.transactionTypes.selectedItem.short_name === "ПРЕМ") {
        bonus_fine_type_id = Number(this.transactionReasons.selectedReason.value)
        id_reason = null
      } else if (this.transactionTypes.selectedItem.short_name === "ОТП" || this.transactionTypes.selectedItem.short_name === "БЛН") {
        vacation_id = Number(this.transactionReasons.selectedReason.value)
        id_reason = null
      } 

      const params = {
        id_worker: Number(this.workersList.selectedEmployee.value),
        id_user: Number(this.userId),
        type: this.isEditableMode ? this.recordData.type.value : this.transactionTypes.selectedType,
        transaction_type_id: this.transactionTypes.selectedItem ? Number(this.transactionTypes.selectedItem.value) : null,
        filial_id: this.filials.selectedFilial ? Number(this.filials.selectedFilial.value) : null,
        post_code: this.posts.selectedPost ? this.posts.selectedPost.value : null,
        id_reason: id_reason || null,
        amount: Number(this.transactionAmount),
        date_user: this.transactionDate.value,
        id_worker_author: this.workersList.selectedWorker ? Number(this.workersList.selectedWorker.value) : '',
        company_id: Number(this.companies.selectedCompany.value),
        comment: this.comment,
        money_source_id: this.moneySourcesList.selectedMoneySource ? this.moneySourcesList.selectedMoneySource.value : '',
        bonus_fine_type_id,
        vacation_id,
        sheet_id: this.recordData.sheet_id
      };

      return params;
    },

    validateDate() {
      let startString = this.currentSheet.period.split('-')[0];
      let startParts = startString.split(".");
      let startDate = new Date(+startParts[2], startParts[1] - 1, +startParts[0]); 
      
      let endString = this.currentSheet.period.split('-')[1] ? this.currentSheet.period.split('-')[1] : this.currentSheet.period.split('-')[0];
      let endParts = endString.split(".");
      let endDate = new Date(+endParts[2], endParts[1] - 1, +endParts[0]); 

      let currentString = this.transactionDate.value;
      let currentParts = currentString.split(".");
      let currentDate = new Date(+currentParts[2], currentParts[1] - 1, +currentParts[0]); 

      if (currentDate >= startDate && currentDate <= endDate) {
        // do something
      } else {
        this.transactionDate.error = this.currentSheet.period.split('-')[1] ? `Дата учётная должна быть в интервале ${this.currentSheet.period}` : `Дата учётная должна быть ${this.currentSheet.period}`
      }
    },

    validateForm() {
      this.isSubmitted = true;

      this.validateDate();

      if (this.transactionTypes.selectedItem.short_name === "ЗП КАРТ" || this.transactionTypes.selectedItem.short_name === "ЗП НАЛ" || this.transactionTypes.selectedItem.short_name === "НАЧ ДЕП" || this.transactionTypes.selectedItem.short_name === "СПС ДЕП" || this.transactionTypes.selectedItem.short_name === "ВОЗВ ПДОТЧ" || this.transactionTypes.selectedItem.short_name === "НАЧ ПДОТЧ") {
        return !this.transactionTypesError && !this.filialError && !this.postError && !this.transactionAmountError && !this.transactionDate.error && !this.companiesError;
      } else {
        return !this.transactionTypesError && !this.filialError && !this.postError && !this.transactionReasonsError && !this.transactionAmountError && !this.transactionDate.error && !this.companiesError;
      }
    },

    async submit() {
      if (!this.validateForm() || this.submitIsDisabled) {
        return;
      }
      
      const params = this.getParams();

      this.submitIsDisabled = true;

      await this.$store.dispatch("calculationSheets/createTransactionForType", params)
        .then(res => {
          this.close()
          this.$emit("submitted");

          this.$notify({
            group: "success-message",
            data: {
              textMessage: `Новая проводка успешно создана`,
            },
          });
        })
        .catch((e) => {
          this.$notify({
            group: "error-message",
            data: {
              textMessage: `Произошла ошибка. Попробуйте ещё раз.`,
            },
          });

          const { data } = e.response;

          if (data.message === "Дата учетная (date_user) не входит в диапазон ведомости") {
            this.transactionDate.error = `Дата учётная должна быть в интервале ${this.recordData.period}`
          }
        })
        .finally(res => {
          this.submitIsDisabled = false;
        })
    },

    formatTransactionTypes() {
      if (this.allTransactionTypesFromStore) {
        this.transactionTypes.items = this.allTransactionTypesFromStore
          .filter(item => item.type === (this.isEditableMode ? this.recordData.type.value : this.transactionTypes.selectedType))
          .map((item) => ({
            short_name: item.short_name,
            label: item.short_name + ' ' + item.name,
            formatLabel: `<span style='color: ${item.type === 'withdraw' ? '#ff238d' : '#12c971'}'>${item.short_name}</span> ${item.name}`,
            resultLabel: `<span style='color: ${item.type === 'withdraw' ? '#ff238d' : '#12c971'}'>${item.short_name}</span> ${item.name}`,
            subLabel: item.description,
            value: item.id,
            dropdown_name: item.dropdown_name,
            description: item.description || '',
            type: item.type,
            conditions: item.conditions,
            reasons: item.reasons,
          }))
      }

      if (this.transactionTypes.items.reasons) this.transactionTypes.items.reasons = this.transactionTypes.items.reasons.sort((a, b) => Number(b.sort) - Number(a.sort))
    },

    formatTransactionReasons() {
      this.transactionReasons.items = this.allTransactionReasonsFromStore
        .filter(item => item.type === "common" || item.type === (this.isEditableMode ? this.recordData.type.value : this.transactionTypes.selectedType))
        .map((item) => ({
          label: item.name,
          value: item.id,
          subLabel: item.description || '',
          sort: item.sort
        }));

      this.transactionReasons.items = this.transactionReasons.items.sort((a, b) => Number(b.sort) - Number(a.sort))
    },

    formatWorkersList() {
      this.workersList.items = this.workersListFromStore.map((item) => ({
        label: item.fio,
        value: item.id,
        formatLabel: `
            <div style="display: flex; align-items: center;">
                <div style="width: 34px; height: 34px; border-radius: 50%; background-color: #8e9cbb; margin-right: 10px; overflow: hidden; flex: 0 0 34px">
                    <img src="${item.photo ? item.photo : 'redesign/img/userpic.svg'}" style="width: 100%; height: 100%; object-fit: cover;" />
                </div>
                <div>
                    <div style="">${item.fio}</div>
                    <div style="color: #8E9CBB; font-size: 13px;">${item.filial_name}, ${item.post_names}</div>   
                </div>
            </div>
        `,
        filial_id: item.filial_id ? item.filial_id : null,
        filial_name: item.filial_name ? item.filial_name : null,
        filial_company_id: item.filial_company_id ? item.filial_company_id : null,
        filial_company_name: item.filial_company_name ? item.filial_company_name : null,
        company_id: item.company_id ? item.company_id : null,
        company_name: item.company_name ? item.company_name : null,
        posts: item.posts.map(post => ({
          value: post.code,
          label: post.name
        })),
        post_names: item.post_names,
        photo: item.photo,
      }));

      this.workersList.defaultItems = this.workersList.items;

      let user = this.$store.state.user

      if (user && user.id) {
        let userWorkerIndex = this.workersList.items.findIndex((worker) => worker.value === user.id);
        this.workersList.items.splice(0, 0, this.workersList.items.splice(userWorkerIndex, 1)[0]);

        this.workersList.items[0].formatLabel = `
          <div style="display: flex; align-items: center; border-bottom: 1px solid #DFE4EF;padding-bottom: 10px;">
            <div style="width: 34px; height: 34px; border-radius: 50%; background-color: #8e9cbb; margin-right: 10px; overflow: hidden; flex: 0 0 34px">
              <img src="${user.photo ? user.photo : 'redesign/img/userpic.svg'}" style="width: 100%; height: 100%; object-fit: cover;" />
            </div>
            <div>
              <div style="font-weight: 500;">${user.name}</div>
              <div style="color: #8E9CBB; font-size: 13px;">${user.filial.filial_name}</div>   
            </div>
          </div>
        `

        // this.workersList.items.unshift({
        //   label: user.name,
        //   value: user.id,
        //   formatLabel: `
        //       <div style="display: flex; align-items: center; border-bottom: 1px solid #DFE4EF;padding-bottom: 10px;">
        //         <div style="width: 34px; height: 34px; border-radius: 50%; background-color: #8e9cbb; margin-right: 10px; overflow: hidden; flex: 0 0 34px">
        //           <img src="${user.photo ? user.photo : 'redesign/img/userpic.svg'}" style="width: 100%; height: 100%; object-fit: cover;" />
        //         </div>
        //         <div>
        //           <div style="font-weight: 500;">${user.name}</div>
        //           <div style="color: #8E9CBB; font-size: 13px;">${user.filial.filial_name}</div>   
        //         </div>
        //       </div>
        //     `,
        //   photo: user.photo,
        //   filial_name: user.filial.filial_name,
        //   post_names: user.post_names,
        // })

        this.workersList.selectedWorker = this.workersList.items[0]
      }
    },

    formatCompanies() {
      this.companies.items = [];

      Object.entries(this.companiesFromStore).forEach(([key, value]) => {
        this.companies.items.push({
          value: key,
          label: value
        })
      });
      
      if (this.workersList.selectedEmployee.company_id) {
        let employeeCompany = this.companies.items.find(company => Number(company.value) === this.workersList.selectedEmployee.company_id)
        this.companies.items = this.companies.items.filter(company => Number(company.value) !== this.workersList.selectedEmployee.company_id)
        this.companies.items.unshift({
          value: employeeCompany.value,
          label: employeeCompany.label,
          formatLabel: `<div style='font-weight: 500; border-bottom: 1px solid #DFE4EF;padding-bottom: 10px;'>${employeeCompany.label}</div>`,
        })
        this.companies.selectedCompany = this.companies.items[0]
      } else if (this.workersList.selectedEmployee.filial_company_id) {
        let filialCompany = this.companies.items.find(company => Number(company.value) === this.workersList.selectedEmployee.filial_company_id)
        this.companies.items = this.companies.items.filter(company => Number(company.value) !== this.workersList.selectedEmployee.filial_company_id)
        this.companies.items.unshift({
          value: filialCompany.value,
          label: filialCompany.label,
          formatLabel: `<div style='font-weight: 500; border-bottom: 1px solid #DFE4EF;padding-bottom: 10px;'>${filialCompany.label}</div>`,
        })
        this.companies.selectedCompany = this.companies.items[0]
      }
    },

    sendErrorDateMessage(message) {
      this.transactionDate.error = message
    },
    async getVacationSickList(workerId, category) {
      await this.$store.dispatch("personalEmployee/transactions/fetchVacationSickList", {workerId, category}).then((res) => {
        this.vacationWorker = res[0];

        if (res && res.length) {
          this.transactionReasons.items = res[0].vacations.map(item =>
            {
              //item.from_dt = moment(item.from_dt, "DD.MM.Y").format("DD.MM.Y")
              //item.to_dt = moment(item.to_dt, "DD.MM.Y").format("DD.MM.Y")

            return {
            ...item,
            value: item.id,
            label: item.category === "sick" ? `Больничный &mdash; ${moment(item.from_dt).format("DD.MM.YYYY")}-${moment(item.to_dt).format("DD.MM.YYYY")}` : `${item.type ? item.type.name : ""} &mdash; ${moment(item.from_dt).format("DD.MM.YYYY")}-${moment(item.to_dt).format("DD.MM.YYYY")}`
          }})
        } else {
          this.transactionReasons.items = [];
        }
      })
    }
  },
  watch: {
    "transactionTypes.selectedType"() {
      this.transactionTypes.items = [];
      this.transactionTypes.selectedItem = '';
      this.transactionReasons.items = [];
      this.transactionReasons.selectedReason = '';

      this.formatTransactionTypes();
      this.formatTransactionReasons();

      // this.$store.dispatch("personalEmployee/transactions/fetchTransactionTypes", this.isEditableMode ? this.recordData.type.value : this.transactionTypes.selectedType)
      // this.$store.dispatch("personalEmployee/transactions/fetchTransactionReasons", this.isEditableMode ? this.recordData.type.value : this.transactionTypes.selectedType);
    }, 
    transactionTypesFromStore() {
      this.formatTransactionTypes()
    },
    transactionReasonsFromStore() {
      this.formatTransactionReasons()
    },
    workersListFromStore() {
      this.formatWorkersList()
    },
    companiesFromStore() {
      this.formatCompanies()
    },
    "workersList.selectedEmployee": function (value) {
      this.transactionReasons.selectedReason = '';
      if (!value) {
        this.filials.formatedFilials = this.filials.allFilials;
        this.filials.selectedFilial = "";
        this.posts.selectedPost = "";
        this.companies.selectedCompany = "";
      } else {
        this.filials.formatedFilials = this.filials.allFilials.filter(filial => Number(filial.value) !== this.workersList.selectedEmployee.filial_id)
        this.filials.formatedFilials.unshift({
          value: this.workersList.selectedEmployee.filial_id, 
          label: this.workersList.selectedEmployee.filial_name, 
          formatLabel: `<div style='font-weight: 500; border-bottom: 1px solid #DFE4EF;padding-bottom: 10px;'>${this.workersList.selectedEmployee.filial_name}</div>`,
        })
        this.filials.selectedFilial = this.filials.formatedFilials[0]

        this.posts.selectedPost = this.workersList.selectedEmployee.posts[0]

        if (this.workersList.selectedEmployee.company_id) {
          this.companies.selectedCompany = {
            value: this.workersList.selectedEmployee.company_id,
            label: this.workersList.selectedEmployee.company_name
          }
        } else if (this.workersList.selectedEmployee.filial_company_id) {
          this.companies.selectedCompany = {
            value: this.workersList.selectedEmployee.filial_company_id,
            label: this.workersList.selectedEmployee.filial_company_name
          }
        }

        if (this.transactionTypes.selectedItem.short_name === "ОТП") {
          this.getVacationSickList(this.workersList.selectedEmployee.value, "vacation")
        }

        if (this.transactionTypes.selectedItem.short_name === "БЛН") {
          this.getVacationSickList(this.workersList.selectedEmployee.value, "sick")
        }
      }
      
      if (this.recordData && this.workersList.selectedEmployee && this.workersList.selectedEmployee.posts) {
        this.posts.selectedPost = {
          value: this.workersList.selectedEmployee.posts.find(post => String(post.value) === String(this.recordData.post_code.value))
        }
      } 
    },
    async "transactionTypes.selectedItem"() {
      this.transactionReasons.selectedReason = "";
      if (!this.transactionTypes.selectedItem) {
        this.formatTransactionReasons()
      } else {
        if (this.transactionTypes.selectedItem.short_name === "ШТРФ" || this.transactionTypes.selectedItem.short_name === "ПРЕМ") {
          // await this.$store.dispatch("finesAndBonuses/fetchFinesAndBonuses", this.posts.selectedPost ? this.posts.selectedPost.value : "");
          if (this.transactionTypes.selectedItem.short_name === "ШТРФ") {
            this.transactionReasons.items = this.finesCollection.items;
            this.transactionReasons.groups = this.finesCollection.groups;
          }
          if (this.transactionTypes.selectedItem.short_name === "ПРЕМ") {
            this.transactionReasons.items = this.bonusesCollection.items;
            this.transactionReasons.groups = this.bonusesCollection.groups;
          }
        } else if (this.transactionTypes.selectedItem.short_name === "ОТП" && this.workersList.selectedEmployee) {
          this.getVacationSickList(this.workersList.selectedEmployee.value, "vacation")
        } else if (this.transactionTypes.selectedItem.short_name === "БЛН" && this.workersList.selectedEmployee) {
          this.getVacationSickList(this.workersList.selectedEmployee.value, "sick")
        } else if (this.transactionTypes.selectedItem.reasons) {
          this.transactionReasons.items = Object.values(this.transactionTypes.selectedItem.reasons).map(reason => ({
            value: reason.id,
            label: reason.name,
            subLabel: reason.description,
          }))
        } else {
          this.formatTransactionReasons()
        }
      }
    },
    "filials.selectedFilial"() {
      if (this.filials.selectedFilial && this.transactionTypes.selectedItem && this.transactionTypes.selectedItem.conditions && this.transactionTypes.selectedItem.conditions.length) {
        this.transactionTypes.selectedItem.conditions.forEach(condition => {
          if (condition.filials && condition.filials.length && condition.filials.find(filial => filial.name === this.filials.selectedFilial.label) && condition.money_sources && condition.money_sources.length) {
            this.moneySourcesList.selectedMoneySource = this.moneySourcesList.items.find(moneySource => moneySource.value === condition.money_sources[0].id)
            // this.moneySourcesList.selectedMoneySource = {
            //   value: condition.money_sources[0].id,
            //   label: condition.money_sources[0].name,
            // }
          }
        })
      } else {
        this.moneySourcesList.selectedMoneySource = ""
      }
    },
    "transactionReasons.selectedReason"() {
      if (this.recordData && this.recordData.amount && this.recordData.amount.value && this.isEditInit) {
        this.transactionAmount = this.recordData.amount.value < 0 ? this.recordData.amount.value * -1 : this.recordData.amount.value
        // this.isEditInit = !this.isEditInit
      } else if (this.transactionReasons.selectedReason && this.transactionReasons.selectedReason.amount) {
        this.transactionAmount = this.transactionReasons.selectedReason.amount
      } else {
        this.transactionAmount = ""
      }
    },
    async "posts.selectedPost"() {
      if (this.transactionTypes.selectedItem.short_name === "ШТРФ" || this.transactionTypes.selectedItem.short_name === "ПРЕМ") {
          await this.$store.dispatch("finesAndBonuses/fetchFinesAndBonuses", {post: this.recordData.post_code && this.recordData.post_code.value ? this.recordData.post_code.value : "", personal_history_id: null});
          if (this.transactionTypes.selectedItem.short_name === "ШТРФ") {
            this.transactionReasons.items = this.finesCollection.items;
            this.transactionReasons.groups = this.finesCollection.groups;
          }
          if (this.transactionTypes.selectedItem.short_name === "ПРЕМ") {
            this.transactionReasons.items = this.bonusesCollection.items;
            this.transactionReasons.groups = this.bonusesCollection.groups;
          }
        }
    },
    "transactionReasons.items"() {
      if (this.recordData && this.recordData.bonus_fine_type_id && (this.transactionTypes.selectedItem.short_name === "ШТРФ" || this.transactionTypes.selectedItem.short_name === "ПРЕМ")) {
        this.transactionReasons.selectedReason = this.transactionReasons.items.find(item => Number(item.value) === Number(this.recordData.bonus_fine_type_id.value))
      } else if (this.recordData && this.recordData.vacation_id && (this.transactionTypes.selectedItem.short_name === "ОТП" || this.transactionTypes.selectedItem.short_name === "БЛН")) {
        this.transactionReasons.selectedReason = this.transactionReasons.items.find(item => Number(item.value) === Number(this.recordData.vacation_id.value))
      } else if (this.recordData && this.recordData.reason_id) {
        this.transactionReasons.selectedReason = this.transactionReasons.items.find(item => Number(item.value) === Number(this.recordData.reason_id.value))
      }
    },
    "filials.formatedFilials"() {
      if (this.isEditableMode) this.filials.selectedFilial = this.filials.formatedFilials.find(filial => filial.value === this.recordData.filial_id.value)
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/vars.scss";
@import "~@/assets/styles/grid.scss";

::v-deep {
  .sidesheet {
    &__header {
      display: none;
    }
    &__wrapper {
      padding: 0 !important;
      z-index: 1001;
    }

    &__content {
      height: 100%;
      padding: 0;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    &__close {
      padding-right: 10px;
      padding-top: 10px;
    }
  }

  .loader-overlay {
    border: unset;
  }
}

.modal-frame {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .col {
    margin-bottom: 35px;
  }

  &__worker {
    display: flex;

    &-photo {
      margin-right: 10px;
    }
    &-text {
      font-weight: 500;
      font-size: 15px;
      line-height: 110%;
    }
    &-name {
    }
    &-posts {
      font-size: 13px;
      line-height: 120%;
    }
    &-filial {
      font-size: 13px;
      line-height: 120%;
      color: #8E9CBB;
    }
  }

  &__transaction-type {
    font-size: 15px;
    line-height: 18px;

    &-name {
    }
    &-description {
      white-space: pre-wrap;
    }
  }

  /deep/ {
    .c-select-required {
      position: static;
      margin-top: 5px;
    }

    .c-select__input.c-select__input_required {
      &:focus {
        border-color: #ff238d;
      }
    }

    .scroll-container {
      padding: 35px 30px;

      @include vp-767 {
        padding: 30px 20px;
      }
    }
  }
  
  .h-switcher {
    /deep/ {
      .c-switcher-group {
        display: flex;
      }
    }
  }

  .modal-wrapper {
    flex-grow: 1;
    display: flex;
    overflow: hidden;
  }

  .modal-track {
    width: 100%;
    height: 100%;

    /deep/ {
      .scroll-container {
        padding-right: 0;
      }
    }
  }

  .modal-actions {
    flex-shrink: 0;
    background: white;
    display: none;

    & button {
      flex-basis: 100%;
      width: 100%;
      border-radius: 0;
      flex-shrink: 1;
    }

    @include vp-767 {
      display: flex;
    }
  }

  .form-group {
    max-width: 564px;
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    margin-bottom: 25px;

    &__column {
      width: 100%;
      position: relative;

      &--2 {
        width: calc((100% - 25px) / 2);
        
        &:not(:last-child) {
          margin-right: 25px;
        }

        @include vp-767 {
          width: 100%;

          &:not(:last-child) {
            margin-right: 0;
            margin-bottom: 25px;
          }
        }
      }

      &--3 {
        width: calc((100% - 25px * 2) / 3);
        
        &:not(:last-child) {
          
          margin-right: 25px;
        }

        @include vp-767 {
          width: 100%;

          &:not(:last-child) {
            margin-right: 0;
            margin-bottom: 25px;
          }
        }
      }
    }

    &__item-description {
      position: absolute;
      top: 50%;
      right: -40px;
      transform: translateY(-50%);
      white-space: pre-wrap;

      &.c-button_icon_info-new {
        border: unset;
        padding: 0;
        font-size: 13px;
        color: $color-primary;
        align-items: center;

        /deep/ {
          .c-button-label {
            margin-top: 4px;
          }
        }
      }

      &--mobile {
        display: none;
      }

      &--desktop {
        margin: 4px 0 0;

        /deep/ {
          .c-button-icon {
            transform: translate(-50%, -50%);
          }
        }
      }


      @include vp-767 {
        position: relative;
        transform: unset;
        top: unset;
        right: unset;
        margin-top: 15px;

        &--desktop {
          display: none;
        }

        &--mobile {
          display: flex;
          height: fit-content;
        }
      }
    }
  }

  &__header {
    margin-bottom: 10px;
  }

  &__title {
    margin-bottom: 12px;
    font-size: 18px;
    font-weight: 500;
  }

  &__actions {
    margin-bottom: 12px;
    display: flex;
    align-items: center;

    @include vp-767 {
      display: none;
    }
  }

  &__action {
    &:not(:last-child) {
      margin-right: 10px;
    }
  }

  @include bootstrapMd {
    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    &__title {
      font-size: 22px;
      margin-bottom: 0;
    }
    &__actions {
      margin-bottom: 0;
    }
  }
}



// .violation-type {
//   &__wrapper {
//     padding: 30px 20px 20px;
//   }

//   &__category {
//     padding-top: 6px;
//   }

//   .is-invalid {
//     padding-bottom: 20px;
//   }

//   &__actions {
//     display: none;
//     flex-shrink: 0;
//   }
// }

// @include bootstrapMd {
//   .violation-type {
//     @include gridGenerator(11);

//     padding-left: 30px;
//     padding-right: 87px;
//     padding-bottom: 40px;
//     padding-top: 25px;

//     &__importance {
//       padding-top: 14px;
//     }

//     &__wrapper {
//       padding: 0 0 40px;
//     }

//     &__actions {
//       display: flex;
//       & button {
//         margin-right: 10px;
//       }
//     }
//   }
//   .modal-frame {
//     .modal-actions {
//       display: none;
//     }
//   }
// }

// .modal-frame__subtitle {
//   font-size: 15px;
// }

// .form-group {
//   max-width: 564px;
// }
// .modal-frame__header {
//   margin-bottom: 20px;
// }
// .modal-frame__subtitle {
//   margin-bottom: 30px;
// }
// .modal-frame__subtitle-short-name {
//   &--withdraw {
//     color: $pink;
//   }

//   &--deposit {
//     color: $color-success;
//   }
// }

// .modal-frame__conditions-title {
//   font-weight: 500;
//   font-size: 18px;
//   line-height: 22px;
// }
// .modal-frame__conditions-list {
//   list-style: none;
//   padding: 0;
//   margin-top: 25px;
// }
// .modal-frame__conditions-item {
//   display: grid;
//   grid-template-columns: 1fr 1fr auto;
//   gap: 30px;
//   align-items: center;

//   &:not(:last-child) {
//     margin-bottom: 30px;
//   }

//   @include vp-767 {
//     grid-template-columns: auto;
//   }
// }
// .modal-frame__conditions-item-remove {
//   opacity: 0;
//   pointer-events: none;
//   transition: 0.3s ease-in-out;

//   &.isActive {
//     opacity: 1;
//     pointer-events: all;
//   }

//   @include vp-767 {
//     display: none;
//   }
// }

// .modal-frame__conditions-item-header {
//   display: none;

//   @include vp-767 {
//     display: flex;
//     align-items: center;
//     justify-content: space-between;
//     font-weight: 500;
//     font-size: 15px;

//     .modal-frame__conditions-item-remove {
//       display: flex;
//     }
//   }
// }

/deep/ {
  .c-button.close-popover {
    cursor: pointer;
    background: none;
    border: 1px solid #d3d8df;
    height: 38px;
    border-radius: 19px;
    padding: 0 30px;
    color: #000;
    white-space: nowrap;
    -webkit-transition: 0.2s;
    transition: 0.2s;
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -ms-flex-wrap: nowrap;
    flex-wrap: nowrap;
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
    -webkit-box-pack: center;
    -ms-flex-pack: center;
    justify-content: center;
    font-size: 15px;
    font-weight: 500;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
    -ms-flex-negative: 0;
    flex-shrink: 0;
    position: relative;
    margin: 0 auto;
  }
}
/deep/ {
  .modal-frame .modal-track .scroll-container {
    padding-right: 20px;
  }
} 

.modal-frame__transaction-type-name {
  font-weight: 500;
}

/deep/ {
  .c-input-container-list-item-label {
    width: 100%;
    white-space: unset;
  }
  .c-button-ok {
    display: none!important;
  }

  @include vp-1279 {
    .c-button-ok {
      display: block!important;
    }
  }
}

.show-description {
  height: fit-content;
  margin-top: 15px;
  margin-left: 20px;

  /deep/ {
    .modal-padding {
      padding: 0;
    }
  }

  @include vp-767 {
    margin: -10px 0 20px 10px;
  }
}

.calculation-sheet {
  &__actions {
    /deep/ {
      .c-button__dropdown__container {
        width: 300px;
        max-width: 92vw;
      }
    }

    @include vp-767 {
      /deep/ {
        .c-button__label__left {
          display: none;
        }
        .c-button__wrapper {
          padding: 0;
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .c-button__icon__right {
          margin: 0;
        }
      }
    }
  }
  &__filial {
    display: flex;
    align-items: center;
    font-size: 15px;
    margin-bottom: 26px;

    &-icon {
      width: 12px;
      height: 15px;
      margin-right: 10px;
    }
  }
  &__filters {
    margin-top: 25px;

    .col {
      margin-bottom: 25px;
    }
  }
  &__table {
    &-header {
      display: flex;
      align-items: center;
      justify-content: flex-end;

      button {
        &:not(:last-child) {
          margin-right: 10px;
        }
      }
    }
  }

  /deep/ {
    .layout-header {
      margin-bottom: 10px;

      @include vp-767 {
        flex-direction: row;
        height: auto;
      }
    }
    .layout-header-actions {
      @include vp-767 {
        margin-top: 0;
      }
    }
    .subheader {
      flex-direction: column;
    }
  }
}
.form-group__item-balances {
  display: flex;
}
.form-group__item-balances-item {
  &:not(:last-child) {
    margin-right: 20px;
  }
}
.form-group__item-balances-item-header {
  display: flex;
  align-items: center;
}
.form-group__item-balances-item-number {
  color: #8E9CBB;
  font-size: 18px;
  font-weight: 500;
  line-height: normal;

  &.positive {
    color: #12C971;
  }
  &.negative {
    color: #FF238D;
  }
}
.form-group__item-balances-item-name {
  font-size: 13px;
  line-height: 110%;
  margin-top: 5px;
}
.form-group__balances-description {
  margin-left: 10px;
  /deep/ {
    .c-button-icon {
      transform: translate(-50%, -50%);
    }
  }
}

/deep/ {
  .tablet {
    display: none;

    @include vp-1023 {
      cursor: pointer;
      background: none;
      border: 1px solid #d3d8df;
      height: 38px;
      border-radius: 19px;
      padding: 0 30px;
      color: #000;
      white-space: nowrap;
      -webkit-transition: 0.2s;
      transition: 0.2s;
      display: -webkit-box;
      display: -ms-flexbox;
      display: flex;
      -ms-flex-wrap: nowrap;
      flex-wrap: nowrap;
      -webkit-box-align: center;
      -ms-flex-align: center;
      align-items: center;
      -webkit-box-pack: center;
      -ms-flex-pack: center;
      justify-content: center;
      font-size: 15px;
      font-weight: 500;
      -webkit-box-sizing: border-box;
      box-sizing: border-box;
      -ms-flex-negative: 0;
      flex-shrink: 0;
      position: relative;
      margin: 0 auto;
    }
  }
}

.row-sum {
  margin-top: 10px;
}
</style>
